import { useState, useRef, useEffect } from "react";
import "./NavMenu.css";
import { NavLink, useLocation } from "react-router-dom";
import { useFilteredNavData } from "./NewNavData";
import { Menu, Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function NavMenu() {
  const { t } = useTranslation();
  const location = useLocation();
  const filteredNavData = useFilteredNavData(location.pathname);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const timeoutRef = useRef(null);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const menuRef = useRef(null);

  const isActiveRoute = (path) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const handleMouseEnter = (index) => {
    if (isMobileView) return;
    clearTimeout(timeoutRef.current);
    setHoveredItem(index);
    setIsSubmenuOpen(true);
  };

  const handleMouseLeave = () => {
    if (isMobileView) return;
    timeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setIsSubmenuOpen(false);
    }, 300); // 300ms delay before closing
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleItemClick = (index) => {
    if (isMobileView) {
      // Toggle submenu in mobile view
      setHoveredItem(hoveredItem === index ? null : index);
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMobileView && isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileView, isMenuOpen]);

  // Watch for window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      if (!mobile && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isMenuOpen]);

  const isAlertsItem = (item) => {
    return item.path === "/alert" || item.label === "Alerts";
  };

  return (
    <div className="nav-container" ref={menuRef}>
      {isMobileView && (
        <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          {isMenuOpen ? <Close /> : <Menu />}
        </div>
      )}

      <div
        className={`new-nav-menu ${isMobileView ? "mobile" : ""} ${
          isMenuOpen ? "open" : ""
        }`}
      >
        {filteredNavData?.map((item, index) => (
          <div
            key={index}
            className="nav-item-wrapper"
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleItemClick(index)}
          >
            <NavLink
              to={item.path}
              className={({ isActive }) =>
                isActive || isActiveRoute(item.path)
                  ? "new-sub-data active-link"
                  : "new-sub-data"
              }
              onClick={(e) => {
                if (item.onClick) {
                  e.preventDefault();
                  item.onClick();
                }
              }}
            >
              <span
                className={`nav-icon ${isAlertsItem(item) ? "has-badge" : ""}`}
              >
                {item.icon}
                {isAlertsItem(item) && <span className="alert-badge"></span>}
              </span>
              {!isMobileView &&
                (hoveredItem === index || isActiveRoute(item.path)) && (
                  <span className=".nav-item-label">{t(item.label)}</span>
                )}
              {isMobileView && (
                <span className="nav-item-label">{t(item.label)}</span>
              )}
            </NavLink>
            {((isMobileView && hoveredItem === index) ||
              (!isMobileView && hoveredItem === index)) &&
              item.subItems &&
              item.subItems.length > 0 && (
                <div
                  className={`submenu ${isMobileView ? "mobile-submenu" : ""}`}
                  onMouseEnter={() => clearTimeout(timeoutRef.current)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.subItems.map((subItem, subIndex) => (
                    <NavLink
                      key={subIndex}
                      to={subItem.path}
                      className={({ isActive }) =>
                        isActive
                          ? "submenu-item active-submenu"
                          : "submenu-item"
                      }
                      onClick={(e) => {
                        if (subItem.onClick) {
                          e.preventDefault();
                          subItem.onClick();
                        }
                      }}
                    >
                      {subItem.icon && (
                        <span className="submenu-icon">{subItem.icon}</span>
                      )}
                      <span className="submenu-label">{t(subItem.label)}</span>
                    </NavLink>
                  ))}
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
}
